// Settings for the "Hook ideas" generator in the video editor: the editable
// prompt and the chosen LLM model, persisted as a per-browser preference (like
// the font size). Also the list of external custom-GPT "bridges" the user can
// open with the prompt + script pre-copied.

import { MODEL_OPTIONS, DEFAULT_GENERATION_MODEL } from 'pages/VideoCreative/models';

export { MODEL_OPTIONS, DEFAULT_GENERATION_MODEL };

// Bumped to -v2 so the new built-in default replaces any old cached prompt in a
// browser that used the previous default.
export const HOOK_PROMPT_STORAGE_KEY = 'video-editing-hook-prompt-v2';
export const HOOK_MODEL_STORAGE_KEY = 'video-editing-hook-model';

// The variable in the instruction template that gets replaced with the video's
// script when generating. Editable in the dialog's Default prompt tab.
export const SCRIPT_VARIABLE = '{{script}}';

// The team-shared default prompts live in the generic dashboard settings store
// (Firebase) under this namespace, one key per prompt — no bespoke endpoint.
export const HOOK_PROMPT_SETTINGS_NAMESPACE = 'video-hook-prompt';
export const HOOK_PROMPT_SETTINGS_KEY = 'prompt';
export const HEADLINE_PROMPT_SETTINGS_KEY = 'headlinePrompt';

// Local per-browser cache for the headline template (mirrors the hook one).
export const HEADLINE_PROMPT_STORAGE_KEY = 'video-editing-headline-prompt-v2';

// The built-in default instruction template. Uses {{script}} to mark where the
// video's script is inserted. This is only the fallback — the shared team default
// is stored server-side (Firebase) and overrides this when present.
export const DEFAULT_HOOK_PROMPT = `You are a direct-response UGC hook writer for Dosaze sleep products.
I’ll paste a video script. Write exactly 5 on-screen text hooks suggestions for the first 4 seconds.
Each hook should:

Be short, punchy, and instantly readable
Create curiosity without relying on empty clickbait
Clearly call out or qualify the right viewer
Connect to a specific problem, symptom, sleep habit, or frustration from the script
Make the ideal customer think, “That’s exactly what happens to me”
Feel natural for TikTok, Reels, and UGC ads
Sound like a confession, realization, warning, or unexpected observation—not polished brand copy
Prioritize qualified attention over broad attention. The goal is not to get the most clicks; it is to attract people who genuinely relate to the problem and may be interested in the product.
Strong examples:

“Your pillow is doing this to your spine all night.”
“My chiropractor told me to switch pillows… honestly, I get why.”
“This is honestly the best sleep investment I’ve ever made.”
“You should not be using pillows like this if you’re a side sleeper.”
“If you’re a side sleeper and you ever wake up with…”


Avoid generic hooks like:

“This pillow changed my life.”
“You need this pillow.”
“Watch this before buying a pillow.”
“The secret to better sleep.”
“Struggling with neck pain?”
Do not make cure claims, guarantees, or unsupported medical statements.
Return:

Exactly 5 hook options
One clear winner
One sentence explaining why it attracts the right viewer
Focus only on the on-screen text hooks. Ignore visual directions unless I say otherwise.
SCRIPT:
${SCRIPT_VARIABLE}`;

// The built-in default headline template (Meta ad headlines). Uses {{script}}
// where the script/concept is inserted. Overridden by the shared team default.
export const DEFAULT_HEADLINE_PROMPT = `You are writing Meta ad headlines for Dosaze sleep products.

Based on the script or ad concept I provide, write 5 unique headlines that make the customer want to take the next step in the funnel.

Follow these rules

- Keep every headline customer-focused
- Lead with the customer’s pain point, desired result, curiosity, trust, urgency, or offer
- Keep headlines concise, clear, and easy to understand
- Make them feel specific to the script rather than generic sleep-product copy
- Use Dosaze or the word pillow, comforter, or sheets when it improves clarity
- Include one offer-focused headline when a BOGO, free gift, discount, or bonus is mentioned
- Include one risk-reversal headline when a 60-night trial or free returns are mentioned
- Use the remaining headlines for benefits, pain points, curiosity, credibility, or urgency
- Do not repeat headlines or closely reword options from previous batches
- Do not use colons
- Do not use exaggerated medical claims
- Do not say the product cures, treats, or eliminates a condition
- Use softer, credible wording such as support, comfort, tension, stiffness, cooler sleep, or better rest
- Do not make every headline start with the same word
- Write in natural Title Case
- Return only the 5 headlines with no explanation

Examples of the preferred style:

Wake Up Without That Stiff Neck Feeling

Stop Letting Jaw And Neck Pain Ruin Your Sleep

Put This Cooling Comforter To The 60-Night Test

Get 2 Supportive Pillows For The Price Of 1

Because Real Cooling Lasts All Night

Here is the new script or ad concept:

${SCRIPT_VARIABLE}`;

// Build the final prompt from the instruction template and the script: swap the
// {{script}} variable for the script; if the template has no variable, append
// the script so an older/edited template still gets it.
export function composeHookPrompt(instructions: string, script: string): string {
  const trimmed = (instructions || '').trim();
  if (trimmed.includes(SCRIPT_VARIABLE)) return trimmed.split(SCRIPT_VARIABLE).join(script);
  return `${trimmed}\n\nScript:\n${script}`;
}

// One prompt that asks a custom GPT to return BOTH on-screen hooks AND Meta ad
// headlines from a single script, so one paste gets both back. Each block's own
// {{script}} placeholder is stripped; the script is appended once at the end.
export function composeJointHookHeadlinePrompt(hookInstructions: string, headlineInstructions: string, script: string): string {
  const stripScript = (s: string) => (s || '').split(SCRIPT_VARIABLE).join('').trim();
  const joint = [
    'From the single script below, produce TWO clearly labeled sections:',
    '',
    '════ PART 1 — ON-SCREEN TEXT HOOKS ════',
    stripScript(hookInstructions),
    '',
    '════ PART 2 — META AD HEADLINES ════',
    stripScript(headlineInstructions),
    '',
    'Return both parts, each under its own heading.',
    '',
    SCRIPT_VARIABLE,
  ].join('\n');
  return composeHookPrompt(joint, script);
}

// Custom GPTs to hand off to when the user wants one of these specific tools.
// (OpenAI has no API for consumer GPTs, so we open them in a new tab with the
// prompt + script copied to the clipboard for the user to paste.)
export interface GptBridge {
  label: string;
  url: string;
}
export const GPT_BRIDGES: GptBridge[] = [
  { label: 'AdScripts.io', url: 'https://chatgpt.com/g/g-RJhpbqw4h-adscripts-io' },
  { label: 'Kallaway', url: 'https://chatgpt.com/g/g-683ca272a59481919ba1ae53dd97100c-kallaway' },
  { label: 'AI Marketing / SEO', url: 'https://chatgpt.com/g/g-6938aacca4e08191b0033a75c9dd23c0-ai-business-marketing-prompt-seo-blog-ads' },
];

export function loadHookPrompt(): string {
  try {
    const value = localStorage.getItem(HOOK_PROMPT_STORAGE_KEY);
    if (value != null && value.trim()) return value;
  } catch { /* preference only */ }
  return DEFAULT_HOOK_PROMPT;
}

export function saveHookPrompt(value: string): void {
  try { localStorage.setItem(HOOK_PROMPT_STORAGE_KEY, value); } catch { /* preference only */ }
}

export function loadHookModel(): string {
  try {
    const value = localStorage.getItem(HOOK_MODEL_STORAGE_KEY);
    if (value && MODEL_OPTIONS.some((m) => m.value === value)) return value;
  } catch { /* preference only */ }
  return DEFAULT_GENERATION_MODEL;
}

export function saveHookModel(value: string): void {
  try { localStorage.setItem(HOOK_MODEL_STORAGE_KEY, value); } catch { /* preference only */ }
}

export function loadHeadlinePrompt(): string {
  try {
    const value = localStorage.getItem(HEADLINE_PROMPT_STORAGE_KEY);
    if (value != null && value.trim()) return value;
  } catch { /* preference only */ }
  return DEFAULT_HEADLINE_PROMPT;
}

export function saveHeadlinePrompt(value: string): void {
  try { localStorage.setItem(HEADLINE_PROMPT_STORAGE_KEY, value); } catch { /* preference only */ }
}
